'use client'
import { animated, useSpringRef, useTransition } from '@react-spring/web'
import { useEffect, useRef, useState } from 'react'

import { DialogTrigger } from 'component/ui/dialog'
import { useIsMobile } from 'store/server-context/device'
import { usePickerOpenStore } from 'store/global/picker-open'
import calculateMainLeft from 'util/calculateMainLeft'
import cn from 'util/cn'
import DropdownMenuButton from './dropdownMenuTitle'
import { translate } from './utils'

interface ILocationPickerButton {
  currentTitle: {
    index: number
    title: string
  } | null
  disabled?: boolean
  showArrowIcon?: boolean
  menuTitles: { id: string; title: string }[]
  theme?: 'light' | 'still'
}
const LocationPickerButton = (props: ILocationPickerButton) => {
  const {
    currentTitle,
    disabled,
    menuTitles,
    showArrowIcon = true,
    theme = 'light'
  } = props

  const [index, setIndex] = useState(currentTitle?.index || 0)
  const isMobile = useIsMobile()
  const indexRef = useRef(index)
  const showCard = usePickerOpenStore(state => state.pickerOpen)
  const transRef = useSpringRef()
  const [left, setLeft] = useState(() => calculateMainLeft())

  const { from, leave } = translate(indexRef.current, index)
  const transitions = useTransition(index, {
    ref: transRef,
    keys: null,
    from: { opacity: 0, transform: from },
    enter: { opacity: 1, transform: 'translate3d(0,0%,0)' },
    leave: { opacity: 0, transform: leave },
    config: { duration: 160 },
    exitBeforeEnter: true,
    onStart: () => {
      indexRef.current = index
    }
  })

  useEffect(() => {
    transRef.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  useEffect(() => {
    if (!currentTitle) {
      return
    }

    if (menuTitles.length === 0) {
      setIndex(0)
      return
    }

    if (
      currentTitle.index !== index &&
      currentTitle.title !== menuTitles[index]?.title
    ) {
      setIndex(currentTitle.index)
    }
  }, [currentTitle, index, menuTitles])

  useEffect(() => {
    if (isMobile) {
      return
    }
    const handleResize = () => {
      const left = calculateMainLeft()
      setLeft(left)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isMobile])

  return (
    <DialogTrigger asChild={true} disabled={disabled}>
      <div
        style={{ left }}
        className={cn(
          !isMobile &&
            'fixed top-[32px] z-header -translate-x-1/2 -translate-y-1/2 transform'
        )}
      >
        {menuTitles.length > 0 &&
          transitions(
            (style, i) =>
              menuTitles[i] && (
                <animated.div key={menuTitles[i].id} style={style}>
                  <DropdownMenuButton
                    menuTitle={menuTitles[i].title}
                    showCard={showCard}
                    showArrowIcon={showArrowIcon}
                    theme={theme}
                  />
                </animated.div>
              )
          )}
      </div>
    </DialogTrigger>
  )
}

export default LocationPickerButton
